import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  isPushSupported,
  getNotificationPermission,
  getCurrentSubscription,
  setupPushNotifications,
  unsubscribeFromPush,
} from '@/shared/api/pushService';
import { notificationApi } from '../entities/notification';

const NotificationContext = createContext<any>(null);

/**
 * Push notification state machine:
 *   idle → loading → ready | error
 *   ready + action → actionPending → ready | error
 */
const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ACTION_PENDING: 'action_pending',
  ERROR: 'error',
};

export const NotificationProvider = ({ children }: any) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(STATUS.IDLE);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [hasBrowserSubscription, setHasBrowserSubscription] = useState(false);
  const [pushPreferenceEnabled, setPushPreferenceEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);

  // Snapshot for rollback on failed actions
  const snapshotRef = useRef<any>(null);

  const loading = status === STATUS.IDLE || status === STATUS.LOADING;
  const actionLoading = status === STATUS.ACTION_PENDING;

  // Computed: Push is active if permission + subscription + preference
  const isPushActive = pushSupported && pushPermission === 'granted' && hasBrowserSubscription && pushPreferenceEnabled;

  // Save current state for rollback
  const takeSnapshot = useCallback(() => {
    snapshotRef.current = {
      hasBrowserSubscription,
      pushPreferenceEnabled,
      pushPermission,
      emailEnabled,
    };
  }, [hasBrowserSubscription, pushPreferenceEnabled, pushPermission, emailEnabled]);

  // Restore state from snapshot
  const rollback = useCallback(() => {
    if (!snapshotRef.current) return;
    setHasBrowserSubscription(snapshotRef.current.hasBrowserSubscription);
    setPushPreferenceEnabled(snapshotRef.current.pushPreferenceEnabled);
    setPushPermission(snapshotRef.current.pushPermission);
    setEmailEnabled(snapshotRef.current.emailEnabled);
    snapshotRef.current = null;
  }, []);

  // Load initial state — only fetch backend preferences when authenticated
  const loadStatus = useCallback(async () => {
    setStatus(STATUS.LOADING);
    try {
      const supported = isPushSupported();
      setPushSupported(supported);

      if (supported) {
        setPushPermission(getNotificationPermission());
        try {
          const subscription = await getCurrentSubscription();
          setHasBrowserSubscription(!!subscription);
        } catch {
          setHasBrowserSubscription(false);
        }
      }

      if (user) {
        try {
          const response = await notificationApi.getPreferences();
          setPushPreferenceEnabled(response?.pushEnabled ?? false);
          setEmailEnabled(response?.emailEnabled ?? true);
        } catch {
          setPushPreferenceEnabled(false);
          setEmailEnabled(true);
        }
      }

      setStatus(STATUS.READY);
    } catch (error) {
      console.error('Error loading notification status:', error);
      setStatus(STATUS.ERROR);
    }
  }, [user]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Enable push notifications (with rollback on failure)
  const enablePush = useCallback(async () => {
    if (!pushSupported) throw new Error('Push notifications not supported');
    if (pushPermission === 'denied') throw new Error('Push notifications blocked by browser');

    takeSnapshot();
    setStatus(STATUS.ACTION_PENDING);

    try {
      // 1. Subscribe browser if needed
      if (!hasBrowserSubscription) {
        const result = await setupPushNotifications();
        if (!result.success) {
          if (result.permission === 'denied') setPushPermission('denied');
          throw new Error(result.error || 'Failed to setup push');
        }
        // Optimistic update
        setHasBrowserSubscription(true);
        setPushPermission('granted');

        // Send subscription to backend
        await notificationApi.subscribePush(result.subscription);
      }

      // 2. Update backend preference
      await notificationApi.updatePreferences({ emailEnabled, pushEnabled: true });
      setPushPreferenceEnabled(true);

      snapshotRef.current = null;
      setStatus(STATUS.READY);
      return { success: true };
    } catch (error) {
      console.error('Error enabling push:', error);
      rollback();
      setStatus(STATUS.READY);
      throw error;
    }
  }, [pushSupported, pushPermission, hasBrowserSubscription, emailEnabled, takeSnapshot, rollback]);

  // Disable push notifications (with rollback on failure)
  const disablePush = useCallback(async () => {
    takeSnapshot();
    setStatus(STATUS.ACTION_PENDING);

    try {
      if (hasBrowserSubscription) {
        await unsubscribeFromPush();
        setHasBrowserSubscription(false);
        try {
          await notificationApi.unsubscribePush();
        } catch {}
      }

      await notificationApi.updatePreferences({ emailEnabled, pushEnabled: false });
      setPushPreferenceEnabled(false);

      snapshotRef.current = null;
      setStatus(STATUS.READY);
      return { success: true };
    } catch (error) {
      console.error('Error disabling push:', error);
      rollback();
      setStatus(STATUS.READY);
      throw error;
    }
  }, [hasBrowserSubscription, emailEnabled, takeSnapshot, rollback]);

  // Toggle push
  const togglePush = useCallback(async () => {
    return isPushActive ? disablePush() : enablePush();
  }, [isPushActive, enablePush, disablePush]);

  // Update email preference (with rollback)
  const setEmailPreference = useCallback(
    async (enabled: any) => {
      takeSnapshot();
      setStatus(STATUS.ACTION_PENDING);

      try {
        await notificationApi.updatePreferences({ emailEnabled: enabled, pushEnabled: pushPreferenceEnabled });
        setEmailEnabled(enabled);
        snapshotRef.current = null;
        setStatus(STATUS.READY);
        return { success: true };
      } catch (error) {
        console.error('Error updating email preference:', error);
        rollback();
        setStatus(STATUS.READY);
        throw error;
      }
    },
    [pushPreferenceEnabled, takeSnapshot, rollback]
  );

  const refreshStatus = useCallback(() => {
    loadStatus();
  }, [loadStatus]);

  const value = {
    // State
    pushSupported,
    pushPermission,
    hasBrowserSubscription,
    pushPreferenceEnabled,
    emailEnabled,
    loading,
    actionLoading,
    status,

    // Computed
    isPushActive,

    // Actions
    enablePush,
    disablePush,
    togglePush,
    setEmailPreference,
    refreshStatus,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotificationStatus = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    return {
      pushSupported: false,
      pushPermission: 'default',
      hasBrowserSubscription: false,
      pushPreferenceEnabled: false,
      emailEnabled: true,
      loading: true,
      actionLoading: false,
      status: STATUS.IDLE,
      isPushActive: false,
      enablePush: async () => {},
      disablePush: async () => {},
      togglePush: async () => {},
      setEmailPreference: async () => {},
      refreshStatus: () => {},
    };
  }
  return context;
};

export default NotificationContext;
